import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FacetFilter, FieldFilter, SearchEvent } from '@shared/models/search.model';
import { Brand, DataRef, Domain, DomainMarketLang, Language, LocalizedKeyValue, Market } from '@shared/models/dataref.model';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { lastValueFrom, Observable, of, Subject } from 'rxjs';
import { RefDataService } from '@services/ref-data.service';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Utils } from '@shared/utils/utils';
import { RecipeFilterType, SearchRecipeFormGroup } from '@shared/models/recipe/recipe.model';
import { AcpResponseAPI, RecipeSearchField } from '@shared/models/recipe/recipe-acp.model';
import { SearchFiltersService } from '@services/search/search-filters.service';
import { SavedSearchFilter } from '@shared/models/search/saved-search-filters.model';
import { UserPreferencesService } from '@services/user/user-preferences.service';

export interface DomainMarketGroup {
  domain: Domain;
  markets: Market[];
  selectedMarket?: Market;
}
@Component({
  selector: 'app-recipes-search-query',
  templateUrl: './recipes-search-query.component.html',
  styleUrls: ['./recipes-search-query.component.scss'],
  standalone: false,
})
export class RecipesSearchQueryComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
    domains: Domain[] | null = [];
  @Input()
    brands: Brand[] = [];
  @Input()
    markets: Market[] = [];
  @Input()
    languages: Language[] = [];
  @Input()
    fieldFilters: FieldFilter[] = [];
  @Input()
    facetFilters: FacetFilter[] = [];
  @Input() statuses!: DataRef[];
  @Input() isPopup = false;

  @Output()
    searchChange = new EventEmitter<SearchEvent>();
  @Output()
    domainMarkets: EventEmitter<DomainMarketGroup> = new EventEmitter<DomainMarketGroup>();

  filteredDomains: { [group: string]: Domain[] } = {};
  filteredMarkets: Market[] = [];
  filteredLanguages: Language[] = [];

  queryForm: FormGroup<SearchRecipeFormGroup> = this.formBuilder.group({
    brand: new FormControl(),
    domain: new FormControl(),
    market: new FormControl(),
    lang: new FormControl<Language | null>({ value: null, disabled: true }),
    searchTxt: new FormControl(),
  });

  readonly noDomain: Domain = { key: 'NO_DOMAIN' } as Domain;

  fieldsFilterDomainMarketLangBrand = {
    domainFieldFilter: 'domain.key',
    marketFieldFilter: 'market.key',
    langFieldFilter: 'lang.key',
    brandFieldFilter: 'brand.key',
  };

  private readonly destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private refDataService: RefDataService,
    private readonly searchFiltersService: SearchFiltersService,
    private readonly userPreferencesService: UserPreferencesService,
  ) {}

  ngOnInit(): void {
    this.registerBrandChanges();
    this.registerDomainChanges();
    this.registerMarketChanges();
    this.registerLangChanges();
    this.loadMarkets();
    this.registerFilterChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.domains?.currentValue) {
      this.filteredDomains = this.searchFiltersService.groupDomainBySSID(changes.domains.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  search(): void {
    this.searchChange.emit({
      q: this.queryForm.value.searchTxt ?? '',
      fieldFilters: this.fieldFilters,
      facetFilters: this.facetFilters,
    });
  }

  searchAcp(event: AcpResponseAPI): void {
    this.queryForm.patchValue({ searchTxt: event.query });

    this.updateFieldFilters(event?.fieldFilters?.ingredient, RecipeSearchField.INGREDIENT);
    this.updateFieldFilters(event?.fieldFilters?.recipe, RecipeSearchField.RECIPE);

    this.search();
  }

  removeFacetFilter(index: number): void {
    this.facetFilters.splice(index, 1);
    this.search();
  }

  removeFacetFilters(): void {
    this.facetFilters = [];
    this.search();
  }

  getDomainsSSID(): string[] {
    return Object.keys(this.filteredDomains);
  }

  isBrandSelected(): boolean {
    const selectedBrand = this.getSelected<Brand>(RecipeFilterType.BRAND.toLowerCase());
    return selectedBrand !== null;
  }

  compareByKey(a: Market | null, b: Market | null): boolean {
    return a?.key === b?.key;
  }

  async applyFieldFilters(): Promise<void> {
    const marketKey = this.getSingleFilterValue(this.fieldFilters, this.fieldsFilterDomainMarketLangBrand.marketFieldFilter) ?? '';
    const langKey = this.getSingleFilterValue(this.fieldFilters, this.fieldsFilterDomainMarketLangBrand.langFieldFilter) ?? '';
    const domainKey = this.getSingleFilterValue(this.fieldFilters, this.fieldsFilterDomainMarketLangBrand.domainFieldFilter) ?? '';
    const brandKey = this.getSingleFilterValue(this.fieldFilters, this.fieldsFilterDomainMarketLangBrand.brandFieldFilter) ?? '';

    const brand = this.brands.find((b) => b.key === brandKey);
    this.computeFilteredDomains(brand);
    const allDomains = Object.values(this.filteredDomains).reduce<Domain[]>((acc, arr) => acc.concat(arr), []);
    const domain = allDomains.find((d) => d.key === domainKey);
    await this.computeFilteredMarkets(domain, brand);

    const market = this.filteredMarkets.find((m) => m.key === marketKey);
    this.computeFilteredLanguages(market, brand, domain);

    const lang = this.filteredLanguages.find((l) => l.key === langKey) ?? null;
    this.queryForm.patchValue({ brand, domain, market, lang }, { emitEvent: false });

    this.handleDisabledLangField();
    // Send value to copy popin
    if (domain) {
      this.domainMarkets.emit({
        domain,
        markets: this.filteredMarkets,
        selectedMarket: market,
      });
    }
  }

  private updateFieldFilters(fieldFilters: { field: string; values: string[] } | undefined, field: string): void {
    if (fieldFilters) {
      if (fieldFilters.values.length > 0) {
        this.fieldFilters.unshift({ field, values: fieldFilters.values });
      } else {
        this.fieldFilters = this.fieldFilters.filter((fieldFilter) => fieldFilter.field !== field);
      }
    }
  }

  private registerBrandChanges(): void {
    this.registerChanges(RecipeFilterType.BRAND.toLowerCase());
  }

  private registerDomainChanges(): void {
    this.registerChanges(RecipeFilterType.DOMAIN.toLowerCase());
  }

  private registerMarketChanges(): void {
    this.registerChanges(RecipeFilterType.MARKET.toLowerCase());
  }

  private registerLangChanges(): void {
    this.registerChanges(RecipeFilterType.LANG.toLowerCase());
  }

  private registerChanges(key: string): void {
    Utils.ensure(this.queryForm.get(key))
      .valueChanges.pipe(
        tap((value) => {
          let keyFilter = this.fieldFilters.find((fieldFilter) => fieldFilter.field === key + '.key');
          if (keyFilter) {
            this.fieldFilters.splice(this.fieldFilters.indexOf(keyFilter), 1);
          }
          // Special for no domain
          if (value?.key === this.noDomain.key) {
            keyFilter = {
              field: 'domain.key',
              values: (this.domains ?? []).map((domain) => domain.key),
              type: 'exclusion',
            };
            this.fieldFilters.push(keyFilter);
          } else if (value) {
            keyFilter = {
              field: key + '.key',
              values: [value.key],
            };
            this.fieldFilters.push(keyFilter);
          }
        }),
        switchMap((value: LocalizedKeyValue) => this.addMoreActions(key, value)),
        tap(() => this.search()),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private addMoreActions(key: string, value: Brand | Domain | Market | LocalizedKeyValue | undefined): Observable<void> {
    switch (key) {
    case RecipeFilterType.BRAND.toLowerCase():
      return this.handleBrandAction(value as Brand);
    case RecipeFilterType.DOMAIN.toLowerCase():
      return this.handleDomainAction(value as Domain | undefined);
    case RecipeFilterType.MARKET.toLowerCase():
      return this.handleMarketAction(value as Market | undefined);
    default:
      return of(void 0);
    }
  }

  private handleBrandAction(brand: Brand): Observable<void> {
    return of(void 0).pipe(
      tap(() => {
        const activatedAreaDomains = (brand?.activatedAreas ?? []).map((activatedArea) => activatedArea.domain.key);
        this.filteredDomains = this.searchFiltersService.groupDomainBySSID(
          (this.domains ?? []).filter((domain) => activatedAreaDomains.includes(domain.key)),
        );
        this.checkAndResetDomainSelection(brand?.activatedAreas);
        this.filteredMarkets = this.filterMarketsList(this.markets, brand);
        this.checkAndResetMarketSelection(this.filteredMarkets);
      }),
      map(() => void 0),
    );
  }

  private checkAndResetDomainSelection(activatedArea: DomainMarketLang[]): void {
    const selectedDomain = this.getSelected<Domain>(RecipeFilterType.DOMAIN.toLowerCase());
    if ((activatedArea || []).find((domainMarketLang) => selectedDomain?.key === domainMarketLang.domain.key)) {
      this.queryForm.patchValue({
        domain: selectedDomain,
      });
    } else {
      this.queryForm.patchValue({
        domain: null,
      });
    }
  }

  private handleDomainAction(domain?: Domain): Observable<void> {
    const selectedBrand = this.getSelected<Brand>(RecipeFilterType.BRAND.toLowerCase());
    if (selectedBrand) {
      return of(void 0).pipe(
        tap(() => {
          this.filteredMarkets = this.filterMarketsList(this.markets, selectedBrand, domain);
          if (this.filteredMarkets.length === 1) {
            this.patchMarketValue(this.filteredMarkets[0]);
          } else {
            this.checkAndResetMarketSelection(this.filteredMarkets);
          }
        }),
      );
    } else if (domain) {
      return this.refDataService.getByKey('markets', true, domain.key).pipe(
        tap((response) => {
          this.filteredMarkets = this.refDataService.translateReference(this.refDataService.toArray(response));
          // emit domain & filtered markets selection on domain select
          this.domainMarkets.emit({
            domain,
            markets: this.filteredMarkets,
            selectedMarket: undefined,
          });
        }),
        map(() => void 0),
      );
    } else {
      return of(void 0).pipe(
        tap(() => {
          this.loadMarkets();
        }),
      );
    }
  }

  private filterMarketsList(allMarkets: Market[], brand: Brand, domain?: Domain): Market[] {
    const brandDomainAvailableMarkets = this.getBrandDomainAvailableMarkets(brand, domain);
    return allMarkets.filter((market) => brandDomainAvailableMarkets.includes(market.key));
  }

  private checkAndResetMarketSelection(availableMarkets: Market[]): void {
    if ((availableMarkets || []).length === 1) {
      this.patchMarketValue(availableMarkets[0]);
    } else {
      const selectedMarket = this.getSelected<Market>(RecipeFilterType.MARKET.toLowerCase());
      if (selectedMarket && availableMarkets.find((market) => market.key === selectedMarket.key)) {
        this.patchMarketValue(selectedMarket);
      } else {
        this.patchMarketValue(null);
      }
    }
  }

  private handleMarketAction(market?: Market): Observable<void> {
    return of(void 0).pipe(
      tap(() => {
        const selectedBrand = this.getSelected<Brand>(RecipeFilterType.BRAND.toLowerCase());
        if (market?.langs) {
          const selectedDomain = this.getSelected<Domain>(RecipeFilterType.DOMAIN.toLowerCase());
          if (selectedBrand) {
            const availableLangKeys = this.getBrandDomainMarketAvailableLangs(selectedBrand, market, selectedDomain);
            this.filteredLanguages = this.languages.filter((lang) => availableLangKeys.includes(lang.key));
          } else {
            const marketLangKeys = (market.langs as Array<Language>).map((lang: Language) => lang.key);
            this.filteredLanguages = this.languages.filter((lang) => marketLangKeys.includes(lang.key));
          }
          if (this.filteredLanguages.length === 1) {
            this.queryForm.patchValue({
              lang: this.filteredLanguages[0],
            });
          } else {
            this.queryForm.patchValue({
              lang: null,
            });
          }
          this.queryForm.get(RecipeFilterType.LANG.toLowerCase())?.enable();
          this.loadAndFilterMarketDomains(market, selectedBrand);
          // Emit selectedMarket when market is selected
          if (selectedDomain) {
            this.domainMarkets.emit({
              domain: selectedDomain,
              markets: this.filteredMarkets,
              selectedMarket: market,
            });
          }
        } else {
          this.filteredLanguages = [];
          this.queryForm.patchValue({
            lang: this.filteredLanguages[0],
          });
          this.queryForm.get(RecipeFilterType.LANG.toLowerCase())?.disable();
          this.loadDomains(selectedBrand);
        }
      }),
    );
  }

  private getBrandDomainAvailableMarkets(brand: Brand, domain?: Domain): string[] {
    return (brand?.activatedAreas || [])
      .filter((domainMarketLang) => (domain && domain.key !== this.noDomain.key ? domainMarketLang.domain.key === domain.key : true))
      .map((domainMarketLang) => domainMarketLang.market.key);
  }

  private getBrandDomainMarketAvailableLangs(brand: Brand, market: Market, domain?: Domain): string[] {
    return brand.activatedAreas
      .filter((domainMarketLang) => (domain && domain.key !== this.noDomain.key ? domainMarketLang.domain.key === domain.key : true))
      .filter((domainMarketLang) => domainMarketLang.market.key === market.key)
      .map((domainMarketLang) => domainMarketLang.lang.key);
  }

  private getSelected<T>(formControlName: string): T {
    return Utils.ensure(this.queryForm.get(formControlName)).value;
  }

  /**
   * Modify value for market selector
   * And ensure that market and lang selectors are correctly enabled/disabled
   */
  private patchMarketValue(marketValue: Market | null): void {
    this.queryForm.patchValue({
      market: marketValue,
    });

    this.queryForm.get(RecipeFilterType.LANG.toLowerCase())?.enable();
  }

  private loadDomains(brand: Brand): void {
    this.filteredDomains = this.searchFiltersService.loadDomains(this.domains ?? [], brand);
  }

  private loadMarkets(): void {
    this.searchFiltersService
      .getMarkets()
      .pipe(takeUntil(this.destroy$))
      .subscribe((markets) => {
        this.filteredMarkets = markets;
      });
  }

  private loadAndFilterMarketDomains(market: Market, brand?: Brand): void {
    this.searchFiltersService
      .getFilteredDomainsByMarket(market, this.domains, brand)
      .pipe(takeUntil(this.destroy$))
      .subscribe((filteredDomains) => {
        this.filteredDomains = filteredDomains;
      });
  }

  private getSingleFilterValue(filters: FieldFilter[], field: string): string | undefined {
    const value = filters.find((f) => f.field === field)?.values?.[0];
    return typeof value === 'string' ? value : undefined;
  }

  private registerFilterChanges(): void {
    this.userPreferencesService.selectedFilter$
      .pipe(
        filter((saved): saved is SavedSearchFilter => !!saved),
        takeUntil(this.destroy$),
      )
      .subscribe((saved) => {
        this.applySavedFilters(saved);
      });
  }

  private async applySavedFilters(saved: SavedSearchFilter): Promise<void> {
    this.fieldFilters = [...(saved.fieldFilters ?? [])];
    this.facetFilters = [...(saved.facetFilters ?? [])];

    await this.applyFieldFilters();

    this.search();
  }

  private handleDisabledLangField(): void {
    const langCtrl = this.queryForm.get(RecipeFilterType.LANG.toLowerCase());
    if (this.filteredLanguages.length > 0) {
      langCtrl?.enable({ emitEvent: false });
    } else {
      langCtrl?.disable({ emitEvent: false });
    }
  }

  private async computeFilteredMarkets(domain?: Domain, brand?: Brand): Promise<void> {
    if (brand) {
      this.filteredMarkets = this.filterMarketsList(this.markets, brand, domain);
    } else if (domain) {
      const response = await lastValueFrom(this.refDataService.getByKey('markets', true, domain.key));
      this.filteredMarkets = this.refDataService.translateReference(this.refDataService.toArray(response));
    } else {
      this.filteredMarkets = await lastValueFrom(this.searchFiltersService.getMarkets());
    }
  }

  private computeFilteredDomains(brand?: Brand): void {
    if (brand) {
      const activatedAreaDomains = (brand?.activatedAreas ?? []).map((activatedArea) => activatedArea.domain.key);
      this.filteredDomains = this.searchFiltersService.groupDomainBySSID(
        (this.domains ?? []).filter((d) => activatedAreaDomains.includes(d.key)),
      );
    } else {
      this.filteredDomains = this.searchFiltersService.groupDomainBySSID(this.domains ?? []);
    }
  }

  private computeFilteredLanguages(market?: Market, brand?: Brand, domain?: LocalizedKeyValue): void {
    if (market?.langs) {
      if (brand) {
        const availableLangKeys = this.getBrandDomainMarketAvailableLangs(brand, market, domain);
        this.filteredLanguages = this.languages.filter((lang) => availableLangKeys.includes(lang.key));
      } else {
        const marketLangKeys = (market.langs as Array<Language>).map((lang: Language) => lang.key);
        this.filteredLanguages = this.languages.filter((lang) => marketLangKeys.includes(lang.key));
      }
    } else {
      this.filteredLanguages = [];
    }
  }
}
